import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, FileText, Clock, RefreshCw, TrendingUp, AlertTriangle, CheckCircle2, ArrowUpCircle } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

// Cooldown global de 6h pour l'analyse IA (même entre chantiers différents)
const COOLDOWN_HEURES = 6;

interface UsageQuotasCardProps {
  className?: string;
}

export function UsageQuotasCard({ className }: UsageQuotasCardProps) {
  const {
    plan,
    planName,
    limits,
    usage,
    getAnalyseIAUsagePercent,
    getOCRUsagePercent,
    getRemainingAnalysesIA,
    getRemainingOCR,
    refreshSubscription,
    loading,
    nextResetDate,
    billingCycleStart,
  } = useSubscription();

  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calcul du prochain reset (basé sur la date de souscription)
  const getNextResetDate = () => {
    // Si on a une date de reset calculée depuis le billing cycle
    if (nextResetDate) {
      const resetDate = new Date(nextResetDate);
      return resetDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    }

    // Fallback: 1er du mois prochain (pour plan free ou si pas de billing cycle)
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  // Navigation vers la page des plans
  const handleUpgrade = () => {
    navigate('/settings/subscription');
  };

  // Refresh manuel
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshSubscription();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Couleur selon le pourcentage d'utilisation
  const getUsageColor = (percent: number) => {
    if (percent >= 90) return 'text-red-500';
    if (percent >= 70) return 'text-orange-500';
    if (percent >= 50) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getProgressColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-orange-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const analyseIAPercent = getAnalyseIAUsagePercent();
  const ocrPercent = getOCRUsagePercent();
  const remainingAnalysesIA = getRemainingAnalysesIA();
  const remainingOCR = getRemainingOCR();

  // Déterminer si illimité
  const isAnalyseIAUnlimited = limits.max_analyses_ia === -1;
  const isOCRUnlimited = limits.max_ocr === -1;

  if (loading) {
    return (
      <Card className={cn("animate-pulse", className)}>
        <CardHeader>
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-2/3 mt-2"></div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="h-20 bg-muted rounded"></div>
          <div className="h-20 bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle>Utilisation mensuelle</CardTitle>
              <CardDescription>
                Suivi de vos quotas Scan IA et Analyse IA
              </CardDescription>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={cn("w-4 h-4 text-muted-foreground", isRefreshing && "animate-spin")} />
          </button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ══════════════════════════════════════════════════════════════════
            SECTION ANALYSE IA
            ══════════════════════════════════════════════════════════════════ */}
        <div className="p-4 bg-muted/30 rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-purple-500/20 rounded-lg">
                <Sparkles className="w-4 h-4 text-purple-500" />
              </div>
              <span className="font-medium">Analyse IA</span>
            </div>
            {isAnalyseIAUnlimited ? (
              <Badge variant="secondary" className="bg-purple-500/20 text-purple-500 border-purple-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Illimité
              </Badge>
            ) : (
              <span className={cn("text-sm font-semibold tabular-nums", getUsageColor(analyseIAPercent))}>
                {usage.analyses_ia_used} / {limits.max_analyses_ia}
              </span>
            )}
          </div>

          {!isAnalyseIAUnlimited && (
            <>
              {/* Barre de progression */}
              <div className="space-y-1.5">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn("h-full rounded-full transition-all duration-500", getProgressColor(analyseIAPercent))}
                    style={{ width: `${Math.min(analyseIAPercent, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{analyseIAPercent.toFixed(0)}% utilisé</span>
                  <span>{remainingAnalysesIA} restante{remainingAnalysesIA > 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Alerte si quota presque épuisé */}
              {analyseIAPercent >= 80 && (
                <div className={cn(
                  "flex items-center justify-between gap-2 p-2 rounded-lg text-xs",
                  analyseIAPercent >= 90
                    ? "bg-red-500/10 text-red-500"
                    : "bg-orange-500/10 text-orange-500"
                )}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      {analyseIAPercent >= 100
                        ? "Quota atteint."
                        : `${remainingAnalysesIA} analyse${remainingAnalysesIA > 1 ? 's' : ''} restante${remainingAnalysesIA > 1 ? 's' : ''}`
                      }
                    </span>
                  </div>
                  {plan !== 'entreprise' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleUpgrade}
                      className={cn(
                        "h-6 px-2 text-xs",
                        analyseIAPercent >= 90 ? "text-red-600 hover:text-red-700" : "text-orange-600 hover:text-orange-700"
                      )}
                    >
                      <ArrowUpCircle className="w-3 h-3 mr-1" />
                      Upgrade
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Info Cooldown */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span>Cooldown de {COOLDOWN_HEURES}h entre chaque analyse d'un même chantier</span>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            SECTION OCR / IMPORT DOCUMENTS
            ══════════════════════════════════════════════════════════════════ */}
        <div className="p-4 bg-muted/30 rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <FileText className="w-4 h-4 text-blue-500" />
              </div>
              <span className="font-medium">Scan IA</span>
            </div>
            {isOCRUnlimited ? (
              <Badge variant="secondary" className="bg-blue-500/20 text-blue-500 border-blue-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Illimité
              </Badge>
            ) : (
              <span className={cn("text-sm font-semibold tabular-nums", getUsageColor(ocrPercent))}>
                {usage.ocr_used} / {limits.max_ocr}
              </span>
            )}
          </div>

          {!isOCRUnlimited && (
            <>
              {/* Barre de progression */}
              <div className="space-y-1.5">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn("h-full rounded-full transition-all duration-500", getProgressColor(ocrPercent))}
                    style={{ width: `${Math.min(ocrPercent, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{ocrPercent.toFixed(0)}% utilisé</span>
                  <span>{remainingOCR} scan{remainingOCR > 1 ? 's' : ''} restant{remainingOCR > 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Alerte si quota presque épuisé */}
              {ocrPercent >= 80 && (
                <div className={cn(
                  "flex items-center justify-between gap-2 p-2 rounded-lg text-xs",
                  ocrPercent >= 90
                    ? "bg-red-500/10 text-red-500"
                    : "bg-orange-500/10 text-orange-500"
                )}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      {ocrPercent >= 100
                        ? "Quota atteint."
                        : `${remainingOCR} scan${remainingOCR > 1 ? 's' : ''} restant${remainingOCR > 1 ? 's' : ''}`
                      }
                    </span>
                  </div>
                  {plan !== 'entreprise' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleUpgrade}
                      className={cn(
                        "h-6 px-2 text-xs",
                        ocrPercent >= 90 ? "text-red-600 hover:text-red-700" : "text-orange-600 hover:text-orange-700"
                      )}
                    >
                      <ArrowUpCircle className="w-3 h-3 mr-1" />
                      Upgrade
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Info sur l'OCR */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileText className="w-3.5 h-3.5" />
            <span>Extraction automatique des factures et documents</span>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            FOOTER - Reset et Plan
            ══════════════════════════════════════════════════════════════════ */}
        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Réinitialisation le {getNextResetDate()}</span>
          </div>
          <Badge variant="outline" className="text-xs">
            Plan {planName}
          </Badge>
        </div>

        {/* Tableau récapitulatif des limites du plan */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <p className="text-xs font-medium text-muted-foreground mb-3">Limites de votre plan</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex justify-between p-2 bg-muted/20 rounded-lg">
              <span className="text-muted-foreground">Chantiers</span>
              <span className="font-medium">
                {limits.max_chantiers === -1 ? 'Illimité' : limits.max_chantiers}
              </span>
            </div>
            <div className="flex justify-between p-2 bg-muted/20 rounded-lg">
              <span className="text-muted-foreground">Membres équipe</span>
              <span className="font-medium">
                {limits.max_membres === -1 ? 'Illimité' : limits.max_membres}
              </span>
            </div>
            <div className="flex justify-between p-2 bg-muted/20 rounded-lg">
              <span className="text-muted-foreground">Historique</span>
              <span className="font-medium">
                {limits.historique_annees === -1 ? 'Illimité' : `${limits.historique_annees} an${limits.historique_annees > 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
